
import { TFunction } from 'i18next';
import { Options, Select } from '@/components/Controls/Select';
import { NotFound } from '@/components/Message/NotFound';

type HistoryFilterProps = {
  t: TFunction<undefined>;
  isLoadingAllGroups?: boolean;
  isLoadingAllCompanies?: boolean;
  isLoadingAllBranches?: boolean;
  isLoadingAllClients?: boolean;
  groupOptions?: Options[];
  companyOptions?: Options[];
  branchOptions?: Options[];
  clientTypeOptions?: Options[];
  clientOptions?: Options[];
  groupId?: string;
  companyId?: string;
  branchId?: string;
  clientType?: string;
  clientFullName?: string;
  searchByGroup?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByCompany?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByBranch?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByClientType?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
  searchByClient?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
};

export const HistoryFilter = ({
  t,
  isLoadingAllGroups,
  isLoadingAllCompanies,
  isLoadingAllBranches,
  isLoadingAllClients,
  groupOptions,
  companyOptions,
  branchOptions,
  clientTypeOptions,
  clientOptions,
  groupId,
  companyId,
  branchId,
  clientType,
  clientFullName,
  searchByGroup,
  searchByCompany,
  searchByBranch,
  searchByClientType,
  searchByClient,
}: HistoryFilterProps) => {
  return (
    <div className="flex gap-3">
      <div className="flex flex-1 flex-col">
        <div className="text-gray-600 text-2sm mb-0.5">
          {t('common:group')}
        </div>
        <div>
          {isLoadingAllGroups ? (
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allGroups')}
              options={[]}
            />
          ) : (
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allGroups')}
              options={groupOptions}
              value={groupId || ''}
              onChange={searchByGroup}
            />
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="text-gray-600 text-2sm mb-0.5">
          {t('common:company')}
        </div>
        <div className="min-h-[36px] flex items-center">
          {isLoadingAllCompanies ? (
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allCompanies')}
              options={[]}
            />
          ) : groupId && companyOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:noCompanyLinkedToThisGroup')}
              />
            </div>
          ) : companyOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:youAreNotLinkedToAnyCompany')}
              />
            </div>
          ) : (
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allCompanies')}
              options={companyOptions}
              value={companyId || ''}
              onChange={searchByCompany}
            />
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="text-gray-600 text-2sm mb-0.5">
          {t('common:branch')}
        </div>
        <div className="min-h-[36px] flex items-center">
          {isLoadingAllBranches ? (
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allBranches')}
              options={[]}
            />
          ) : companyId && branchOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:noBranchLinkedToThisCompany')}
              />
            </div>
          ) : groupId && branchOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:noBranchLinkedToThisGroup')}
              />
            </div>
          ) : branchOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:youAreNotLinkedToAnyBranch')}
              />
            </div>
          ) : (
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allBranches')}
              options={branchOptions}
              value={branchId || ''}
              onChange={searchByBranch}
            />
          )}
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="text-gray-600 text-2sm mb-0.5">
          {t('common:clientType')}
        </div>
        <div>
          <Select
            selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
            options={clientTypeOptions}
            value={clientType || ''}
            onChange={searchByClientType}
          />
        </div>
      </div>
      <div className="flex flex-1 flex-col">
        <div className="text-gray-600 text-2sm mb-0.5">
          {t('common:client')}
        </div>
        <div className="min-h-[36px] flex items-center">
          {isLoadingAllClients ? (
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allClients')}
              options={[]}
            />
          ) : clientType && clientOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:noClientLinkedToThisType')}
              />
            </div>
          ) : branchId && clientOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:noClientLinkedToThisBranch')}
              />
            </div>
          ) : companyId && clientOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:noClientLinkedToThisCompany')}
              />
            </div>
          ) : groupId && clientOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:noClientLinkedToThisGroup')}
              />
            </div>
          ) : clientOptions.length === 0 ? (
            <div className="flex items-center">
              <NotFound
                containerClassName="justify-start gap-1"
                iconClassName="-mt-0.5 text-[16.5px]"
                textClassName="text-xs"
                text={t('common:youAreNotLinkedToAnyClient')}
              />
            </div>
          ) : (
            <Select
              selectClassName="h-[36px] bg-gray-FCFCFC dark-mode:bg-coal-500"
              placeholder={t('common:allClients')}
              options={clientOptions}
              value={clientFullName || ''}
              onChange={searchByClient}
            />
          )}
        </div>
      </div>
    </div>
  );
};
